import { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatBlock from "@/components/StatBlock";
import WorldMap from "@/components/WorldMap";
import { fmtNum } from "@/lib/format";

const REGION_COLORS = {
  Europe: "#D4AF37",
  Americas: "#38BDF8",
  Oceania: "#4ADE80",
  Asia: "#A78BFA",
  "Middle East": "#F87171",
  Africa: "#FBBF24",
};

function bucket(g) {
  if (g < 0.1) return { label: "Ultra-low", color: "#4ADE80" };
  if (g < 0.25) return { label: "Low", color: "#34D399" };
  if (g < 0.45) return { label: "Medium", color: "#FBBF24" };
  if (g < 0.65) return { label: "High", color: "#F97316" };
  return { label: "Very high", color: "#F87171" };
}

export default function Geographic() {
  const [items, setItems] = useState([]);
  const [region, setRegion] = useState("All");
  const [selected, setSelected] = useState(null);
  const [metric, setMetric] = useState("grid_kwh");

  useEffect(() => {
    api.get("/world/carbon").then((r) => setItems(r.data.items));
  }, []);

  const regions = ["All", ...Array.from(new Set(items.map((i) => i.region)))];
  const filtered = region === "All" ? items : items.filter((i) => i.region === region);

  const summary = {
    countries: filtered.length,
    averageGrid: filtered.reduce((s, i) => s + i.grid_kwh, 0) / (filtered.length || 1),
    medianTax: (() => {
      const t = [...filtered].map((i) => i.carbon_tax_usd_t).sort((a, b) => a - b);
      return t.length ? t[Math.floor(t.length / 2)] : 0;
    })(),
    netZeroAvg: filtered.reduce((s, i) => s + i.net_zero_year, 0) / (filtered.length || 1),
  };

  const sortedTax = [...filtered].sort((a, b) => b.carbon_tax_usd_t - a.carbon_tax_usd_t).slice(0, 15);

  // Regional drill-down stats
  const cleanestGrid = [...filtered].sort((a, b) => a.grid_kwh - b.grid_kwh)[0];
  const dirtiestGrid = [...filtered].sort((a, b) => b.grid_kwh - a.grid_kwh)[0];
  const earliestNZ = [...filtered].sort((a, b) => a.net_zero_year - b.net_zero_year)[0];
  const latestNZ = [...filtered].sort((a, b) => b.net_zero_year - a.net_zero_year)[0];
  const totalTaxed = filtered.filter((c) => c.carbon_tax_usd_t > 0).length;
  const taxedPct = filtered.length ? (totalTaxed / filtered.length) * 100 : 0;
  const uniqueFrameworks = Array.from(new Set(filtered.flatMap((c) => (c.framework || "").split(/ · | / )))).filter(Boolean);

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Module · Geographic Intelligence"
        title="Where carbon is priced, taxed, and decarbonising."
        subtitle="Country-level grid intensity, carbon taxes, net-zero target years, and applicable green-building frameworks."
        kicker={`${items.length} COUNTRIES TRACKED · ALL REGIONS`}
        guideId="geographic"
      />

      <section className="px-8 pt-8 flex flex-wrap items-center gap-3">
        {regions.map((r) => (
          <button
            key={r}
            data-testid={`region-${r}`}
            onClick={() => setRegion(r)}
            className={`px-3 py-1.5 text-xs border rounded-sm transition-colors ${
              region === r ? "border-gold bg-gold/10 text-gold" : "theme-border theme-ink-muted hover:border-gold/40"
            }`}
          >
            {r}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          {[
            { id: "grid_kwh", label: "Grid intensity" },
            { id: "carbon_tax_usd_t", label: "Carbon tax" },
            { id: "net_zero_year", label: "Net-zero year" },
          ].map((m) => (
            <button
              key={m.id}
              data-testid={`metric-${m.id}`}
              onClick={() => setMetric(m.id)}
              className={`px-3 py-1.5 text-xs border rounded-sm transition-colors ${
                metric === m.id ? "border-gold bg-gold/10 text-gold" : "theme-border theme-ink-muted hover:border-gold/40"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </section>

      <section className="px-8 pt-6 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 h-[480px]">
          <WorldMap
            countries={filtered}
            metric={metric}
            selectedIso2={selected?.iso2}
            onSelect={(c) => setSelected(c)}
          />
        </div>

        <div className="border theme-border theme-surface p-6">
          <div className="overline">Selected country</div>
          {selected ? (
            <>
              <h3 className="font-serif-display text-2xl theme-ink mt-1">{selected.name}</h3>
              <div className="text-xs font-mono-data theme-ink-muted mt-1">
                {selected.iso2} · {selected.iso3} · {selected.region}
              </div>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex items-baseline justify-between">
                  <dt className="theme-ink-muted">Grid intensity</dt>
                  <dd className="font-mono-data text-gold">{selected.grid_kwh.toFixed(3)} kgCO₂e/kWh</dd>
                </div>
                <div className="flex items-baseline justify-between">
                  <dt className="theme-ink-muted">Carbon tax</dt>
                  <dd className="font-mono-data theme-ink">${selected.carbon_tax_usd_t}/tCO₂e</dd>
                </div>
                <div className="flex items-baseline justify-between">
                  <dt className="theme-ink-muted">Net-zero target</dt>
                  <dd className="font-mono-data text-eco">{selected.net_zero_year}</dd>
                </div>
                <div>
                  <dt className="overline mb-1">Frameworks</dt>
                  <dd className="text-xs theme-ink">{selected.framework}</dd>
                </div>
              </dl>
            </>
          ) : (
            <div className="text-sm theme-ink-dim mt-3">Click a country on the map to inspect.</div>
          )}
        </div>
      </section>

      <section className="px-8 pt-6 pb-12">
        <div className="border theme-border theme-surface p-6">
          <div className="overline">KPI stats · {region}</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-3">
            <StatBlock label="Countries" value={fmtNum(summary.countries, 0)} accent="white" tooltip="Countries currently in scope (filtered by selected region)." />
            <StatBlock label="Avg grid intensity" value={fmtNum(summary.averageGrid, 3)} unit="kgCO₂e/kWh" accent="sky" tooltip="Mean grid-electricity emission factor across selected countries." />
            <StatBlock label="Median carbon tax" value={`$${fmtNum(summary.medianTax, 2)}`} unit="USD/tCO₂e" accent="gold" tooltip="Median nominal carbon tax / ETS price in the selected region." />
            <StatBlock label="Avg net-zero target" value={fmtNum(summary.netZeroAvg, 0)} unit="year" accent="eco" tooltip="Mean year of stated net-zero target across selected countries." />
          </div>
        </div>
      </section>

      <section className="px-8 pb-12 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border theme-border theme-surface p-6">
          <div className="overline">Cleanest grid</div>
          <div className="font-serif-display text-2xl theme-ink mt-1">{cleanestGrid?.name || "—"}</div>
          <div className="text-xs theme-ink-muted mt-1 font-mono-data">{cleanestGrid?.grid_kwh?.toFixed(3)} kgCO₂e/kWh · NZ {cleanestGrid?.net_zero_year}</div>
          <div className="overline mt-4">Dirtiest grid</div>
          <div className="font-serif-display text-2xl theme-risk mt-1" style={{color:"#F87171"}}>{dirtiestGrid?.name || "—"}</div>
          <div className="text-xs theme-ink-muted mt-1 font-mono-data">{dirtiestGrid?.grid_kwh?.toFixed(3)} kgCO₂e/kWh · NZ {dirtiestGrid?.net_zero_year}</div>
        </div>

        <div className="border theme-border theme-surface p-6">
          <div className="overline">Net-zero racers</div>
          <div className="text-xs theme-ink-muted">Earliest target</div>
          <div className="font-serif-display text-2xl text-eco mt-1">{earliestNZ?.name || "—"} · {earliestNZ?.net_zero_year}</div>
          <div className="text-xs theme-ink-muted mt-4">Latest target</div>
          <div className="font-serif-display text-2xl theme-ink mt-1">{latestNZ?.name || "—"} · {latestNZ?.net_zero_year}</div>
          <div className="overline mt-4">Countries with a carbon price</div>
          <div className="font-mono-data text-3xl text-gold mt-1">{totalTaxed}/{filtered.length}</div>
          <div className="text-xs theme-ink-muted">{taxedPct.toFixed(2)}% of region</div>
        </div>

        <div className="border theme-border theme-surface p-6">
          <div className="overline">Frameworks active in region</div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {uniqueFrameworks.slice(0, 20).map((f) => (
              <span key={f} className="text-[11px] border theme-border px-2 py-0.5 theme-ink-muted">{f}</span>
            ))}
            {uniqueFrameworks.length === 0 && <span className="text-xs theme-ink-dim">No frameworks listed.</span>}
          </div>
        </div>
      </section>

      <section className="px-8 pb-12">
        <div className="border theme-border theme-surface overflow-hidden">
          <div className="px-6 py-4 border-b theme-border flex items-baseline justify-between">
            <div>
              <div className="overline">All countries · {region}</div>
              <div className="font-serif-display text-xl theme-ink">{filtered.length} entries</div>
            </div>
            <div className="text-xs theme-ink-dim">Click a row to inspect</div>
          </div>
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/[0.02] text-[10px] uppercase tracking-[0.2em] theme-ink-dim sticky top-0">
                <tr>
                  <th className="text-left px-6 py-3">Country</th>
                  <th className="text-left px-3 py-3">Region</th>
                  <th className="text-right px-3 py-3">Grid kgCO₂e/kWh</th>
                  <th className="text-right px-3 py-3">Carbon tax USD/t</th>
                  <th className="text-right px-3 py-3">Net-zero</th>
                  <th className="text-left px-3 py-3">Frameworks</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.iso3}
                    data-testid={`country-row-${c.iso2}`}
                    onClick={() => setSelected(c)}
                    className={`border-t theme-border cursor-pointer hover:bg-black/[0.02] dark:hover:bg-white/[0.02] ${
                      selected?.iso2 === c.iso2 ? "bg-gold/5" : ""
                    }`}
                  >
                    <td className="px-6 py-3">
                      <div className="theme-ink">{c.name}</div>
                      <div className="text-[11px] theme-ink-dim font-mono-data">{c.iso2} · {c.iso3}</div>
                    </td>
                    <td className="px-3 theme-ink-muted">{c.region}</td>
                    <td className="px-3 py-3 text-right font-mono-data text-gold">{c.grid_kwh.toFixed(3)}</td>
                    <td className="px-3 py-3 text-right font-mono-data">${c.carbon_tax_usd_t.toFixed(2)}</td>
                    <td className="px-3 py-3 text-right font-mono-data text-eco">{c.net_zero_year}</td>
                    <td className="px-3 theme-ink-muted text-xs">{c.framework}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="px-8 pb-12">
        <div className="border theme-border theme-surface p-6">
          <div className="overline">Top 15 highest carbon prices</div>
          <div className="font-serif-display text-2xl theme-ink mt-1">Regulatory exposure ranking</div>
          <div className="h-72 mt-3">
            <ResponsiveContainer>
              <BarChart data={sortedTax} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" horizontal={false} />
                <XAxis type="number" stroke="#71717A" />
                <YAxis dataKey="name" type="category" stroke="#71717A" width={120} />
                <Tooltip />
                <Bar dataKey="carbon_tax_usd_t" name="USD/tCO₂e">
                  {sortedTax.map((c) => (
                    <rect key={c.iso2} fill={REGION_COLORS[c.region] || "#D4AF37"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>
    </div>
  );
}
